<template>
  <admin-wrapper
    :straight-title="additionalModuleName"
    :module-enabler="moduleEnabler"
    :is-fetched="isFetched"
    :inputs="inputs"
    @save="save"
    :is-custom-saving="isSaving"
  >
    <template v-for="input in inputs" #[`${input.name}.input`]>
      <v-row :key="input.name" no-gutters>
        <v-col cols="4">
          <data-input
            v-if="input.dataType"
            v-bind="input"
            :value="inputData[input.parentKey]?.[input.attributeName]"
            @change="handleInputChange($event, input.parentKey, input.attributeName)"
          />
          <div v-else :key="input.name" class="mt-6 mb-2 font-weight-medium text-left">{{ input.title }}</div>
        </v-col>
      </v-row>
    </template>
  </admin-wrapper>
</template>
<script>
import { get, call } from 'vuex-pathify';
import AdminWrapper from '@/components/AdminWrapper';
import DataInput from '@/components/DataInput';

export default {
  name: 'SewerAnalysisSettings',
  components: { AdminWrapper, DataInput },
  data: () => ({
    isFetched: false,
    inputData: {},
    isSaving: false,
  }),
  computed: {
    sewerAnalysisFilter: get('admin/settingsValues@sewer_analysis_filter'),
    additionalModuleName: get('admin/additionalModules@SEWER_ANALYSIS.verbose_name'),
    sewerPipesDataSource: get('admin/modulesMapping@sewer.sewer_pipes_data_source'),
    sewerTerminalsDataSource: get('admin/modulesMapping@sewer.sewer_terminals_data_source'),
    datasources: get('layers/metadata'),
    moduleEnabler() {
      return {
        name: 'sewer_analysis_module_enabled',
        straightText: this.additionalModuleName,
        subtitle: this.$i18n.t('admin.moduleEnablerSubtitle', { suffix: this.additionalModuleName }),
      };
    },
    inputs() {
      const inputsData = {
        [this.sewerPipesDataSource]: {
          name: 'pipes',
          attributes: ['net_scope', 'status'],
        },
        [this.sewerTerminalsDataSource]: {
          name: 'terminals',
          attributes: ['net_scope', 'status'],
        },
      };
      return Object.entries(inputsData).reduce((acc, [datasource, data]) => {
        const datasourceData = this.datasources[datasource];
        const items = data.attributes.map(attribute => {
          const attributedata = datasourceData?.attributes_schema.attributes?.find(attr => attr.name === attribute);
          return {
            name: `${datasource}_${attribute}`,
            parentKey: data.name,
            attributeName: attribute,
            dataType: { name: 'select' },
            multiple: true,
            autoConvertSelect: false,
            chips: true,
            items: attributedata?.allowed_values ?? [],
            align: 'start',
            translationPath: this.getTranslationPath('title', attribute, datasource),
            subtitle: this.getTranslationPath('subtitle', attribute, datasource),
          };
        });
        return [
          ...acc,
          {
            name: `title__${datasource}`,
            title: datasourceData?.verbose_name,
            isNamedSlot: false,
          },
          ...items,
        ];
      }, []);
    },
  },
  methods: {
    getDataSources: call('layers/getLayersMetadata'),
    getModulesMapping: call('admin/getModulesMapping'),
    editSingleSetting: call('admin/editSingleSetting'),
    handleInputChange(value, datasource, attribute) {
      this.inputData = {
        ...this.inputData,
        [datasource]: {
          ...this.inputData[datasource],
          [attribute]: value,
        },
      };
    },
    async save() {
      this.isSaving = true;
      try {
        await this.editSingleSetting({
          key: 'sewer_analysis_filter',
          body: {
            value: this.inputData,
          },
        });
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.successEditSetting'),
        });
        this.inputData = { ...this.sewerAnalysisFilter };
      } catch {
        //
      } finally {
        this.isSaving = false;
      }
    },
    getTranslationPath(type, attribute, datasource) {
      /** isStatic variable checks if same attribute in different datasources have to use the same translation */
      const isStatic = ['net_scope'].includes(attribute);
      return `admin.sewerAnalysis.${attribute}_${isStatic ? '' : `${datasource}_`}${type}`;
    },
    async init() {
      this.isFetched = false;
      try {
        await Promise.all([this.getModulesMapping(), this.getDataSources({ with_attributes_schema: true })]);
        this.inputData = { ...this.sewerAnalysisFilter };
        this.isFetched = true;
      } catch {
        //
      }
    },
  },
  mounted() {
    this.init();
  },
};
</script>

<style lang="scss" scoped>
:deep(.v-select__selections) {
  flex-wrap: wrap !important;
  row-gap: 0.5rem;
}
</style>
